import classNames from 'classnames';
import Label from 'components/Label';
import { shouldHideOption } from 'helpers/shouldHideOption';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import { JsonParam, useQueryParam } from 'use-query-params';
import { usePcConfigurator } from 'utils/PcConfiguratorContext';
import PcModelOptionImage from '../PcModelOptionImage';
import styles from './styles';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'

const PcModelComponentImageModal = ({
  isOpen,
  isDataLoaded,
  onClose,
  groupTitle,
  options,
  currentOption,
}) => {
  const { attrTags } = usePcConfigurator();
  const [selections, setSeletions] = useQueryParam('selections', JsonParam);

  const isHidden = (option) => shouldHideOption(attrTags, option);
  const filteredOptions = options.filter((option) => !isHidden(option));
  const selectedItem = filteredOptions.findIndex(
    (option) => option.variant_id === currentOption?.variant_id,
  );

  const customStatusStyle = {
    paddingRight: '10px',
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        preventScroll
        style={{
          overlay: {
            background: 'rgba(255, 255, 255, 0.25)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
          },
          content: {
            width: '100%',
            maxWidth: 'calc(100vh - 200px)',
            borderRadius: '2px',
            margin: '10px',
            overflow: 'hidden',
            border: 'none',
            inset: 'auto',
            background: 'none',
            padding: '0',
          },
        }}
      >
        <Carousel
          autoPlay={false}
          infiniteLoop
          selectedItem={selectedItem}
          showIndicators={false}
          showThumbs={false}
          useKeyboardArrows
          statusFormatter={(current, total) => (
            <span style={customStatusStyle}>{`${current} of ${total}`}</span>
          )}
        >
          {options.map((option) => (
            <div className="item" key={option.id}>
              <div className="item-image">
                <PcModelOptionImage
                  src={option.image?.src}
                  alt={option.title}
                  width={1000}
                  height={1000}
                />
              </div>

              <div className="item-container">
                <div className="option-container">
                  <div className="item-title">
                    {filteredOptions?.[selectedItem]?.sub_group_name
                      ? `${filteredOptions[selectedItem].sub_group_name} - `
                      : ''}
                    {option.title}
                  </div>
                  <div className="price_container">
                    {option.compared_at_price_diff > 0 && (
                      <span className="compared_at_price_diff">
                        <span> &#36;</span>
                        {option.compared_at_price_diff}
                      </span>
                    )}
                    {!!option.price_diff && (
                      <span className={option.price_diff < 0 ? 'red' : 'green'}>
                        <span>{option.price_diff < 0 ? '-' : '+'}</span>
                        <span>&#36;</span>
                        {Math.abs(option.price_diff)}
                      </span>
                    )}
                    {option.is_base && <Label type="blue" text="Base" />}
                    {option.is_recommended && <Label text="Recommended" />}
                    {!option.in_stock && (
                      <Label type="grey" text="Out of Stock" />
                    )}
                  </div>
                </div>

                {isDataLoaded ? (
                  <div className='option-description'>
                    {option.description}
                  </div>
                  ) : (
                    <SkeletonTheme baseColor="#202020" highlightColor="#444">
                      <p>
                        <Skeleton count={2} />
                      </p>
                    </SkeletonTheme>
                  )
                }

                <div
                  className={classNames('selectedButton', {
                    hide: !option.in_stock,
                  })}
                >
                  <button
                    className={classNames('item-button', {
                      disabled: !option.in_stock,
                    })}
                    disabled={!option.in_stock}
                    type="button"
                    onClick={() => {
                      setSeletions({
                        ...selections,
                        [option.group_type]: option.variant_id || '',
                      });
                      onClose();
                    }}
                  >
                    Select this {groupTitle}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </Modal>
      <style jsx>{styles}</style>
    </>
  );
};

PcModelComponentImageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isDataLoaded: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  groupInfo: PropTypes.object.isRequired,
  options: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  currentOption: PropTypes.object.isRequired,
};

export default PcModelComponentImageModal;
